// pages/MarketChat.jsx
// DWI Market Phase 5 — shared chat helpers: auto-refresh (polling every few
// seconds, set in Admin → Market → Settings — not instant chat), private chat
// photos (loaded with the login token), and the "attach photo" button.
import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { marketAPI } from '../services/api';
import { Spinner } from '../components/ui';

// One /status read per page load for the refresh interval.
let refreshPromise = null;
export function chatRefreshSeconds() {
  if (!refreshPromise) {
    refreshPromise = marketAPI.getStatus().then(r => r.chatRefreshSeconds || 5).catch(() => 5);
  }
  return refreshPromise;
}

// Calls fn every `seconds` (or the chat setting) while the tab is visible;
// never overlaps a slow request with the next one.
export function useAutoRefresh(fn, seconds = null, enabled = true) {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  useEffect(() => {
    if (!enabled) return undefined;
    let timer = null; let stopped = false; let busy = false;
    const start = s => {
      if (stopped) return;
      timer = setInterval(async () => {
        if (busy || document.hidden) return;
        busy = true;
        try { await fnRef.current(); } catch { /* next tick tries again */ } finally { busy = false; }
      }, s * 1000);
    };
    if (seconds) start(seconds); else chatRefreshSeconds().then(start);
    return () => { stopped = true; if (timer) clearInterval(timer); };
  }, [seconds, enabled]);
}

// Merge freshly polled messages into the list (no duplicates).
export function mergeMessages(old, fresh) {
  if (!fresh?.length) return old;
  const seen = new Set(old.map(m => m._id));
  return [...old, ...fresh.filter(m => !seen.has(m._id))];
}

export function ChatPhoto({ name, className = 'max-h-56 rounded-lg' }) {
  const [url, setUrl] = useState(null);
  const [big, setBig] = useState(false);
  useEffect(() => {
    let live = true; let made = null;
    marketAPI.chatPhoto(name).then(blob => { if (!live) return; made = URL.createObjectURL(blob); setUrl(made); }).catch(() => {});
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [name]);
  if (!url) return <div className="w-24 h-16 flex items-center justify-center"><Spinner size="sm" /></div>;
  return (
    <>
      <img src={url} alt="" className={`${className} border border-border cursor-pointer mt-1`} onClick={() => setBig(true)} />
      {big && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setBig(false)}>
          <img src={url} alt="" className="max-h-full max-w-full rounded-xl" />
        </div>
      )}
    </>
  );
}

// "📷 Photo" button: uploads one photo, gives back its private name.
export function AttachPhoto({ admin = false, photo, onChange }) {
  const [busy, setBusy] = useState(false);
  const pick = async e => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('Photo is over 5 MB');
    setBusy(true);
    try { onChange((await (admin ? marketAPI.adminChatPhotoUpload(file) : marketAPI.chatPhotoUpload(file))).name); }
    catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };
  return (
    <span className="inline-flex items-center gap-2">
      <label className="px-3 py-1.5 rounded-lg border border-border text-xs text-text-secondary cursor-pointer hover:border-cyan">
        {busy ? <Spinner size="sm" /> : '📷 Photo'}
        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} disabled={busy} />
      </label>
      {photo && <span className="text-[11px] text-green">photo attached <button className="text-text-muted hover:text-red" onClick={() => onChange('')}>×</button></span>}
    </span>
  );
}
